import React from 'react';

import { observer, useSelector } from '@datagrid/state';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, Button, Group, Icon, Stack, TFText, Title } from '@tf/ui';

import { appStore } from '@/core/stores';
import { EditableLabel } from '@/components/internal-tools/EditableLabel';
import { ListItemCard } from '@/components/internal-tools/ListItemCard';
import { Menu } from '@/components/internal-tools/Menu';
import { paths } from '@/components/internal-tools/paths';
import { RefLink } from '@/components/internal-tools/RefLink';

type FieldKind = 'primitive' | 'enum' | 'struct' | 'external' | 'display_only';

type FieldType =
	| 'text'
	| 'number'
	| 'date'
	| 'boolean'
	| 'enum'
	| 'struct'
	| 'label'
	| 'info'
	| 'email'
	| 'phone'
	| 'money';

interface VisualModifier {
	label: string;
	placeholder?: string;
	tooltip?: string;
	preset?: string;
	order?: number;
}

interface FieldDefinition {
	name: string;
	kind: FieldKind;
	type: FieldType;
	visual: VisualModifier;
	isMultiple?: boolean;
	ref?: string;
}

interface StructDefinition {
	name: string;
	kind: 'default' | 'segment' | 'document';
	label: string;
	fields: FieldDefinition[];
}

const StructView: React.FC<{
	structName: string;
	struct: StructDefinition;
	onUpdate: (newStruct: StructDefinition) => void;
}> = observer(({ structName, struct, onUpdate }) => {
	const updateLabel = (fieldToEdit: FieldDefinition, label: string) => {
		const fields = struct.fields.map((f) =>
			f.name === fieldToEdit.name ? { ...fieldToEdit, visual: { ...fieldToEdit.visual, label } } : f
		);
		onUpdate({
			...struct,
			fields,
		});
	};

	const onDelete = (fieldToDelete: FieldDefinition) => {
		const fields = struct.fields.filter((f) => f.name !== fieldToDelete.name);
		onUpdate({
			...struct,
			fields,
		});
	};

	return (
		<Stack gap="16px">
			<Title order={2}>{structName}</Title>

			<Stack gap="4px">
				{struct.fields.map((field) => {
					const typeLink = field.ref
						? field.type === 'struct'
							? paths.structPage(field.ref)
							: paths.enumPage(field.ref)
						: null;

					return (
						<ListItemCard key={field.name}>
							{/* Info */}
							<Box w="100%">
								<EditableLabel
									initialValue={field.visual.label}
									onUpdate={(label) => updateLabel(field, label)}
								/>
								<Group gap="12px" align="baseline">
									<TFText c="dimmed">{field.name}</TFText>

									{/* Type */}
									<TFText c="dimmed" title={field.ref}>
										{field.type}
										{typeLink ? <RefLink to={typeLink} /> : null}
									</TFText>
								</Group>
							</Box>

							{/* Actions */}
							{/* NOTE: removing editing from enums and structs viewer */}
							{/*<Box>*/}
							{/*	<Button size="xs" variant="subtle" color="red" onClick={() => onDelete(field)}>*/}
							{/*		<Icon.IconTrash size={14} />*/}
							{/*	</Button>*/}
							{/*</Box>*/}
						</ListItemCard>
					);
				})}
			</Stack>
		</Stack>
	);
});

export const StructsRoute: React.FC = observer(() => {
	const structs = useSelector(() => appStore.defs.get().structs);
	const structNamesList = Object.keys(structs);

	const params = useParams();
	const navigate = useNavigate();
	const selectedStructName = params.structName ?? '';
	const selectedStruct = structs[selectedStructName] ?? null;

	const updateSelectedStruct = (struct: StructDefinition) => {
		appStore.defs.structs.set((prev) => {
			return {
				...prev,
				[selectedStructName]: struct,
			};
		});
	};

	return (
		<Box sx={{ display: 'flex', gap: '16px' }}>
			<Menu
				items={structNamesList}
				selectedItem={selectedStructName}
				onSelect={(structName) => navigate(paths.structPage(structName))}
			/>

			<Box w="100%">
				{!selectedStruct ? (
					<div>Select struct</div>
				) : (
					<StructView
						structName={selectedStructName}
						struct={selectedStruct}
						onUpdate={updateSelectedStruct}
					/>
				)}
			</Box>
		</Box>
	);
});
